import { mapValues } from "../utils"
import { markRaw } from "vue"
import IFlowConfig from "../IFlowConfig"
import { elementTypeMap } from "../schema/elementTypes"
import { Deserializer } from "../Deserializer"
import { Flow, StepAction } from "./flow"
import {
  IFlow,
  IStep,
  IStepAction,
  IStepElement,
  IStepInputElement,
} from "./flow.types"
import {
  AllowedInputElement,
  ElementRaw,
  IActionRaw,
  IFlowRaw,
  isRawInputElement,
  IStepRaw,
} from "./flowRaw"
import rules from "@/validators/rules"

const parseAction = (
  action: IActionRaw,
  deserializer: Deserializer,
): IStepAction =>
  new StepAction(
    deserializer.deserialize(action.buttonLabel ?? "") as string,
    (condition) => deserializer.deserializeAsync(String(condition)), // booleans are casted to strings, to facilitate typing in flow schemas
    action.buttonType ?? "primary",
    action.nextIf,
    deserializer.deserialize(action.showIf as string),
    deserializer.deserialize(action.showIfNot as string),
    action.nextId,
    action.saveTo ? deserializer.deserializeAction(action.saveTo) : undefined,
    action.to,
    !!deserializer.deserialize(action.disabled as string),
  )

const parseRawElement = (
  element: ElementRaw,
  deserializer: Deserializer,
): IStepElement => ({
  is: markRaw(elementTypeMap[element.is]),

  props: mapValues(element.props, (rawProp) =>
    deserializer.deserialize(rawProp),
  ),

  slots: mapValues(element.slots, (rawSlots) =>
    deserializer.deserialize(rawSlots),
  ),

  isShown: element.showIf
    ? !!deserializer.deserialize(element.showIf as string)
    : element.showIfNot
      ? !deserializer.deserialize(element.showIfNot as string)
      : true,

  showBelowActions: !!element.showBelowActions,
})

const parseRawInputElement = (
  element: AllowedInputElement,
  deserializer: Deserializer,
): IStepInputElement => ({
  ...parseRawElement(element, deserializer),
  rules: !element.optional ? [rules.required] : undefined, // When set in flow definition, rules could be returned from deserializer
  optional: element.optional,
  key: element.key,
  initialValue:
    "value" in element
      ? deserializer.deserialize((element.value as string) ?? "")
      : "",
})

const parseStep = (step: IStepRaw, deserializer: Deserializer): IStep => ({
  title: deserializer.deserialize(step.title)?.toString(),
  description: step.description,
  descriptionHtml: step.descriptionHtml,
  hideHelpText: step.hideHelpText,
  id: step.id,
  elements:
    step.elements?.map((o) =>
      isRawInputElement(o)
        ? parseRawInputElement(o, deserializer)
        : parseRawElement(o, deserializer),
    ) ?? [],
  actions: step.actions?.map((o) => parseAction(o, deserializer)),
})

export const parseRawFlow = (
  raw: IFlowRaw,
  flowConfig: IFlowConfig,
  deserializer: Deserializer,
): IFlow =>
  new Flow(
    raw.steps.map((step) => parseStep(step, deserializer)),
    flowConfig.helpTitle,
    markRaw(flowConfig.helpContent ?? {}),
  )
